import React, {
  forwardRef,
  ComponentType,
  useCallback,
  useMemo,
  useEffect,
  useRef,
  useState,
} from 'react';
import Highlighter from 'react-highlight-words';
import {
  Virtuoso,
  ListProps,
  Components,
  ScrollSeekPlaceholderProps,
  ScrollSeekConfiguration,
} from 'react-virtuoso';
import {
  Checkbox,
  Grid,
  CircularProgress,
  Typography,
  Radio,
  useTheme,
  Paper,
  Box,
  ListItemIcon,
  Tooltip,
  ListItemButton,
  ListItemText,
  listItemButtonClasses,
  listItemTextClasses,
} from '@mui/material';
import ListEmptySearch from './EmptyListIndicator';
import {
  Option,
  SELECT_ITEM_HEIGHT,
  SELECT_EMPTY_ITEM_HEIGHT,
  SELECT_DENSE_ITEM_HEIGHT,
  SelectorStyles,
} from '../types';
import ListItemSkeleton from './ListItemSkeleton';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { TagChip } from 'src/components/TagChip';
import { PSClasses } from 'src/viewsTypes';
import { PerfectScrollbarWrapperProps } from 'src/components/PerfectScrollbar/types';

enum ScrollLimits {
  Over = 160,
  Under = 80,
}

type SelectListProps = {
  options: Option[];
  selectedOptions: Option[];
  filteredOptions: Option[] | null;
  filterValue: string;
  isOnlyOne: boolean;
  isMenuFinishedOpen: boolean;
  isWithController: boolean;
  isWithRowSelectInputs: boolean;
  isBottomControls?: boolean;
  isDense?: boolean;
  menuConfig: {
    MENU_WIDTH: number;
    MENU_HEIGHT: number;
    MENU_TOP_HEIGHT: number;
  };
  style: SelectorStyles;
  handleItemChange: (selectedOption: Option) => void;
};

const highlightStyles = {
  backgroundColor: 'transparent',
  fontWeight: 700,
};

interface OverflowTipProps {
  children: React.ReactNode;
}

const OverflowTip: React.FC<OverflowTipProps> = ({ children }) => {
  const [isOverflowed, setIsOverflow] = useState(false);
  const textElementRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (textElementRef.current) {
      setIsOverflow(
        textElementRef.current.scrollWidth > textElementRef.current.clientWidth,
      );
    }
  }, []);

  return (
    <Tooltip
      title={children}
      sx={{ fontSize: 'small' }}
      placement="right"
      PopperProps={{
        style: {
          zIndex: 1700,
        },
      }}
      disableHoverListener={!isOverflowed}
      disableInteractive
    >
      <div
        ref={textElementRef}
        style={{
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
      >
        {children}
      </div>
    </Tooltip>
  );
};

const scrollSeekConfiguration: ScrollSeekConfiguration = {
  enter: (velocity) => Math.abs(velocity) > ScrollLimits.Over + 20,
  exit: (velocity) => {
    const shouldExit = Math.abs(velocity) < ScrollLimits.Under;
    return shouldExit;
  },
};

const PerfectScrollbarWrapper = React.forwardRef<
  HTMLDivElement,
  PerfectScrollbarWrapperProps
>(({ style, children, ...rest }, ref) => {
  const scrollbarRef = useRef<PerfectScrollbar | null>(null);
  useEffect(() => {
    // updating scroller on load for correct display
    const timer = setTimeout(() => {
      if (scrollbarRef.current) {
        scrollbarRef.current.updateScroll();
      }
    }, 0);

    return () => clearTimeout(timer);
  }, [scrollbarRef.current]);

  return (
    <Box
      component="span"
      height="unset"
      sx={{
        [`& .${PSClasses.RailY}`]: {
          marginTop: ({ spacing }) => spacing(0.25),
          marginBottom: ({ spacing }) => spacing(0.25),
        },
      }}
    >
      {/* @ts-ignore */}
      <PerfectScrollbar
        {...rest}
        style={style}
        options={{
          // minScrollbarLength: 40,
          suppressScrollX: true,
        }}
        ref={scrollbarRef}
        containerRef={(container: HTMLElement) => {
          const divElement = container as HTMLDivElement;

          if (ref) {
            if (typeof ref === 'function') {
              ref(divElement);
            } else {
              ref.current = divElement;
            }
          }
        }}
      >
        {children}
      </PerfectScrollbar>
    </Box>
  );
});

const List = ({
  style,
  options,
  selectedOptions,
  filteredOptions,
  filterValue,
  isOnlyOne,
  menuConfig,
  isDense = false,
  isBottomControls,
  isMenuFinishedOpen,
  isWithRowSelectInputs,
  handleItemChange,
}: SelectListProps) => {
  const primaryStyle = (style.primary ? style.primary : {}) as any;
  const secondaryStyle = (style.secondary ? style.secondary : {}) as any;

  const listHeight = useMemo(() => {
    const itemCount = filteredOptions?.length ?? 0;
    if (!itemCount) return SELECT_EMPTY_ITEM_HEIGHT;

    // height limit set to 250px
    // non-dense item height 58 (x4 = 232, x5 = 290)
    // dense item height 42 (x5 = 210, x6 = 252)
    if (itemCount > (isDense ? 5 : 4)) {
      return menuConfig.MENU_HEIGHT - 2;
    }

    return (
      itemCount * (isDense ? SELECT_DENSE_ITEM_HEIGHT : SELECT_ITEM_HEIGHT)
    );
  }, [menuConfig, filteredOptions]);

  const ListContainer = forwardRef<any, ListProps>(
    ({ children, style }, ref) => {
      const theme = useTheme();
      return (
        <Paper
          sx={{
            borderBottomRightRadius: !isBottomControls ? theme.spacing(0.5) : 0,
            borderBottomLeftRadius: !isBottomControls ? theme.spacing(0.5) : 0,
          }}
        >
          <Box
            ref={ref}
            style={{
              ...style,
              background: theme.palette.common.white,
              borderWidth: '0 1px 1px',
              borderRadius: theme.spacing(0, 0, 0.5, 0.5),
            }}
          >
            {children}
          </Box>
        </Paper>
      );
    },
  );

  const theme = useTheme();
  const isWithSelectIcons = useMemo(() => {
    return Boolean(options.find(({ Icon }) => Boolean(Icon)));
  }, [options]);

  const getListItemStartAdornment = useCallback(
    (
      isOnlyOne: boolean,
      isSelected: boolean,
      option: Option,
      isWithIcons: boolean,
    ) => {
      const { Icon, adornments } = option;
      if (!isWithRowSelectInputs || (isWithIcons && isOnlyOne))
        return (
          <Grid
            component="span"
            sx={{
              height: theme.spacing(4),
              width: theme.spacing(2),
            }}
            marginRight={!Icon && isWithIcons ? theme.spacing(4.5) : 0}
          />
        );

      if (adornments?.start) return adornments.start;

      return isOnlyOne ? (
        <Radio
          checked={isSelected}
          color="primary"
          size={isDense ? 'small' : 'medium'}
        />
      ) : (
        <Checkbox
          color="primary"
          checked={isSelected}
          size={isDense ? 'small' : 'medium'}
        />
      );
    },
    [isWithRowSelectInputs],
  );

  const searchWords = useMemo(() => [filterValue], [filterValue]);

  const cellRenderer = useCallback(
    (index: number, option: Option) => {
      const onClick = (e) => {
        e.stopPropagation();
        handleItemChange(option);
      };

      const {
        value,
        label,
        secondaryLabel,
        Icon,
        adornments,
        tooltipText,
        tag,
      } = option;
      const isSelected = selectedOptions.some((item) => item.value === value);
      return (
        <Tooltip title={tooltipText ?? ''}>
          <ListItemButton
            key={value}
            onClick={onClick}
            selected={isSelected}
            sx={{
              alignItems: 'center',
              height: isDense ? SELECT_DENSE_ITEM_HEIGHT : SELECT_ITEM_HEIGHT,
              fontFamily: theme.typography.fontFamily,
              paddingRight: theme.spacing(1),
              [`& .${listItemButtonClasses.disabled}`]: {
                opacity: 0.25,
              },
              ...(isOnlyOne ? { paddingLeft: 0 } : {}),
            }}
            disabled={'enabled' in option && !option?.enabled}
          >
            {getListItemStartAdornment(
              isOnlyOne,
              isSelected,
              option,
              isWithSelectIcons,
            )}
            {Icon ? (
              <ListItemIcon>
                <Icon
                  sx={{
                    fontSize: ({ spacing }) => spacing(3),
                    color: ({ palette }) =>
                      isSelected
                        ? palette.primary.main
                        : palette.secondary.metalGrey,
                  }}
                />
              </ListItemIcon>
            ) : null}
            <ListItemText
              disableTypography
              sx={{
                textAlign: 'start',
                fontSize: `${isDense ? '0.8' : '0.9'}rem`,
                [`& .${listItemTextClasses.secondary}`]: {
                  fontSize: `${isDense ? '0.7' : '0.8'}rem`,
                },
                ...(isSelected && isOnlyOne ? { fontWeight: 700 } : {}),
              }}
              primary={
                <OverflowTip>
                  <Highlighter
                    unhighlightStyle={{
                      fontSize: `${isDense ? '0.8' : '0.9'}rem`,
                      ...primaryStyle,
                    }}
                    searchWords={searchWords}
                    autoEscape
                    textToHighlight={label}
                    highlightStyle={highlightStyles}
                  />
                </OverflowTip>
              }
              secondary={
                secondaryLabel ? (
                  <OverflowTip>
                    <Highlighter
                      unhighlightStyle={{
                        fontSize: `${isDense ? '0.7' : '0.8'}rem`,
                        opacity: 0.75,
                        ...secondaryStyle,
                      }}
                      searchWords={searchWords}
                      autoEscape
                      textToHighlight={secondaryLabel}
                      highlightStyle={highlightStyles}
                    />
                  </OverflowTip>
                ) : null
              }
            />
            {tag ? <TagChip tag={tag} /> : null}
          </ListItemButton>
        </Tooltip>
      );
    },
    [
      filteredOptions,
      isOnlyOne,
      handleItemChange,
      getListItemStartAdornment,
      filterValue,
      selectedOptions,
    ],
  );

  const ScrollSeekPlaceholder: ComponentType<ScrollSeekPlaceholderProps> =
    useCallback(
      ({ index, height }) => {
        return (
          <ListItemSkeleton
            key={index}
            type={isOnlyOne ? 'singleSelection' : undefined}
            index={index}
            isDense={isDense}
          />
        );
      },
      [isOnlyOne],
    );

  const ListEmptyContainer = useCallback(() => {
    return (
      <div style={{ height: '100%' }}>
        <ListEmptySearch
          searchTerm={filterValue}
          addStyles={{
            ...(!filterValue
              ? {
                  justifyContent: 'center',
                }
              : {}),
          }}
          noSearchTerm={
            options.length ? (
              <CircularProgress />
            ) : (
              <Typography>לא נמצאו אפשרויות</Typography>
            )
          }
          addContainerStyles={{
            borderRadius: isBottomControls
              ? theme.spacing(0.5, 0.5, 0, 0)
              : theme.spacing(0, 0, 0.5, 0.5),
          }}
        />
      </div>
    );
  }, [filterValue, isBottomControls, options]);

  const components: Components<Option> = useMemo(
    () => ({
      List: ListContainer,
      Scroller: PerfectScrollbarWrapper,
      EmptyPlaceholder: ListEmptyContainer,
      ScrollSeekPlaceholder: ScrollSeekPlaceholder,
    }),
    [ListContainer, ListEmptyContainer, ScrollSeekPlaceholder],
  );

  return (
    <Box
      sx={{
        height: listHeight,
        overflowY: 'auto',
        p: 0,
      }}
      component="ul"
    >
      <Virtuoso<Option>
        data={filteredOptions ?? []}
        totalCount={filteredOptions?.length || 0}
        itemContent={cellRenderer}
        components={components}
        scrollSeekConfiguration={scrollSeekConfiguration}
      />
    </Box>
  );
};

export default List;
